import React from "react";
import { eveningPoolRadiusStatus } from "../../utils/eveningPoolRadiusStatus.js";
import { BeakerIcon, BoltIcon, CalendarDaysIcon, ScaleIcon, Square2StackIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { canonicalizeFillRules } from "../../lib/productCompartmentFillRules.js";
import DispatchColorIntensityControl from "../DispatchColorIntensityControl.jsx";
import ProtectAfterReassignmentToggle from "../ProtectAfterReassignmentToggle.jsx";
import SameOdGroupingToggle from "../SameOdGroupingToggle.jsx";
import { HoverCard } from "../ResultsPrimitives.jsx";
import DispatchHeaderActionMenu, { DispatchHeaderMenuSection } from "./DispatchHeaderActionMenu.jsx";
import DispatchHeaderMenuSwitch from "./DispatchHeaderMenuSwitch.jsx";
import { CompartmentSharingHelp, ProductExpansionHelp, ProductMixingHelp } from "./MenuPlusHelps.jsx";
import DispatchCompartmentFillDialog from "../DispatchCompartmentFillDialog.jsx";
import ProductExpansionConfirmation from "./ProductExpansionConfirmation.jsx";
import ConfirmationModal from "../ConfirmationModal.jsx";
import {
  DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  DISPATCH_REASSIGNMENT_MODE_FAST,
  normalizeDispatchReassignmentMode,
  resolveDispatchFastReassignmentEnabled,
} from "../../utils/dispatchReassignmentMode.js";

const isColorPopoverTarget = (target) => Boolean(target?.closest?.('[data-dispatch-color-intensity-popover="true"], [data-product-expansion-confirmation]'));

// Interrupteur du menu Plus ; au survol, une bulle animée compare l'option désactivée et activée.
function ManualProductMixingToggle({
  policy,
  disabled = false,
  label = "Autoriser le mélange de produits",
  Icon = BeakerIcon,
  help = <ProductMixingHelp />,
  isMenuOpen = true,
}) {
  if (!policy) return null;
  const button = <button
      type="button"
      role="menuitemcheckbox"
      aria-label={label}
      aria-checked={policy.enabled}
      aria-busy={policy.isSaving}
      className="dispatch-header-menu-action rounded-[4px]"
      data-keep-menu-open="true"
      tabIndex={-1}
      disabled={disabled || policy.isSaving || !policy.available}
      onClick={() => policy.save(!policy.enabled)}
    >
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">
        {label}
      </span>
      <DispatchHeaderMenuSwitch checked={policy.enabled} isBusy={policy.isSaving} />
    </button>;
  return <div>
    <HoverCard title={label} content={help} placement="left-start" fallbackAxisSideDirection="end" delay={140} maxWidth="min(500px, calc(100vw - 48px))"
      disabled={!isMenuOpen} referenceAsChild flat>{button}</HoverCard>
    {policy.error ? <p role="alert" className="m-0 px-3 py-1 text-xs text-red-700">{policy.error}</p> : null}
  </div>;
}

function CompartmentSharingToggle(props) {
  return <ManualProductMixingToggle {...props} Icon={Square2StackIcon}
    label="Compléter un compartiment" help={<CompartmentSharingHelp />} />;
}

function ProductCompartmentFillToggle({ policy, disabled = false, isMenuOpen = true }) {
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [confirmAnchor, setConfirmAnchor] = React.useState(null);
  const toggleRef = React.useRef(null);
  React.useEffect(() => { if (!isMenuOpen || !policy?.enabled) setConfirmAnchor(null); }, [isMenuOpen, policy?.enabled, policy?.dispatchDate, policy?.dispatchPeriod]);
  React.useEffect(() => { setIsEditorOpen(false); setConfirmAnchor(null); }, [policy?.dispatchDate, policy?.dispatchPeriod]);
  if (!policy) return null;
  const rules = canonicalizeFillRules(policy.rules);
  return <div>
    <div className="flex items-center gap-1 pr-[11px]">
    <div className="min-w-0 flex-1">
    <HoverCard
      title="Expansion des produits"
      content={<ProductExpansionHelp rules={rules} />}
      placement="left-start"
      fallbackAxisSideDirection="end"
      delay={140}
      maxWidth="min(500px, calc(100vw - 48px))"
      disabled={!isMenuOpen || Boolean(confirmAnchor)}
      referenceAsChild
      flat
    >
      <button
        type="button"
        role="menuitem"
        aria-label="Expansion des produits"
        aria-busy={policy.isSaving}
        className="dispatch-header-menu-action rounded-[4px]"
        data-keep-menu-open="true"
        tabIndex={-1}
        disabled={disabled || policy.isSaving || !policy.available}
        onClick={() => policy.enabled ? setConfirmAnchor(toggleRef.current) : policy.save(true)}
      >
        <ScaleIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1" style={{ display: "block" }}>
          <span className="block">Expansion des produits</span>
          <span className="block text-[0.65rem] text-slate-500">{rules.length ? `${rules.length} règle${rules.length > 1 ? "s" : ""} par produit` : "Aucune règle"}</span>
        </span>
      </button>
    </HoverCard>
    </div>
    <button type="button" role="menuitem" tabIndex={-1} aria-label="Régler l’expansion des produits" aria-haspopup="dialog"
      className="shrink-0 rounded-[4px] border border-slate-300 bg-white px-1.5 py-1 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
      disabled={disabled || policy.isSaving || !policy.available}
      onClick={() => setIsEditorOpen(true)}>Régler</button>
    <button ref={toggleRef} type="button" role="menuitemcheckbox" tabIndex={-1}
      aria-label="Expansion des produits" aria-checked={policy.enabled} aria-busy={policy.isSaving}
      data-keep-menu-open="true" className="ml-1 flex shrink-0 items-center rounded-[4px] py-1 focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-45"
      disabled={disabled || policy.isSaving || !policy.available}
      onClick={() => policy.enabled ? setConfirmAnchor(toggleRef.current) : policy.save(true)}>
      <DispatchHeaderMenuSwitch checked={policy.enabled} isBusy={policy.isSaving} />
    </button>
    </div>
    {confirmAnchor ? <ProductExpansionConfirmation anchor={confirmAnchor} policy={policy} disabled={disabled}
      onClose={() => { setConfirmAnchor(null); toggleRef.current?.focus(); }} /> : null}
    {policy.error ? <p role="alert" className="m-0 px-3 py-1 text-xs text-red-700">{policy.error}</p> : null}
    {isEditorOpen ? <DispatchCompartmentFillDialog policy={policy} disabled={disabled} onClose={() => setIsEditorOpen(false)} /> : null}
  </div>;
}

export function EveningOrderPoolMenuItem({ policy, disabled = false, compact = false, isMenuOpen = true }) {
  if (!policy?.visible) return null;
  const detailedRefusals = (policy.summary?.refused_orders || []).filter((item) => item.attempts?.length);
  const explanation = "Pour cette date seulement. Inclut les commandes non affectées et suspendues du Jour pour compléter le Soir, sans retirer de commandes aux tournées du Jour.";
  const button = <button type="button" role="menuitemcheckbox" aria-label="Inclure les commandes non affectées et suspendues du Jour" aria-checked={policy.enabled}
      aria-busy={policy.isSaving} className="dispatch-header-menu-action rounded-[4px]"
      data-keep-menu-open="true" tabIndex={-1}
      disabled={disabled || policy.isSaving || !policy.available}
      onClick={() => policy.save(!policy.enabled)}
      title={compact ? undefined : explanation}>
      <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">{compact ? "Inclure les commandes du Jour" : "Inclure les commandes non affectées et suspendues du Jour"}</span>
      {compact ? <span className="shrink-0 text-xs" aria-hidden="true">{policy.isSaving ? "En cours…" : policy.enabled ? "Activé" : "Désactivé"}</span> : null}
      <DispatchHeaderMenuSwitch checked={policy.enabled} isBusy={policy.isSaving} />
    </button>;
  const summary = <>
    {policy.summary?.message ? <p className="m-0 px-3 py-1 text-xs text-slate-600"><strong>Dernier calcul : </strong>{policy.summary.message}</p> : null}
    {detailedRefusals.length ? <details className="mx-3 my-1 rounded-[4px] border border-slate-200 px-2 py-1 text-xs text-slate-600" data-keep-menu-open="true">
      <summary className="cursor-pointer">Détail des refus ({detailedRefusals.length})</summary>
      <ul className="m-0 grid max-h-48 grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-2 gap-y-1 overflow-y-auto py-1 pl-4">
        {detailedRefusals.map((item) => {
          const radius = eveningPoolRadiusStatus(item.attempts);
          return <li key={item.order_id} className="col-span-3 grid grid-cols-subgrid">
          <strong className="whitespace-nowrap">{item.order_id}</strong>
          <span className="whitespace-nowrap text-slate-600" title={radius.title}>{radius.label}</span>
          <span>{[...new Set(item.attempts.map((attempt) => attempt.reason_detail || attempt.reason_label).filter(Boolean))].join(" ; ") || "Insertion impossible avec les contraintes actuelles."}</span>
        </li>;
        })}
      </ul>
    </details> : null}
  </>;
  return <div className={compact ? "dispatch-header-menu-section" : undefined}>
    {compact ? <HoverCard
      title="Commandes du Jour pour le Soir"
      content={<div data-evening-order-pool-help="true" className="space-y-2 text-xs"><p className="m-0">{explanation}</p>{summary}</div>}
      placement="left-start" width="min(760px, calc(100vw - 32px))" maxWidth="min(760px, calc(100vw - 32px))"
      interactive freezeAfterMs={2000} accentColor="var(--color-accent)"
      disabled={!isMenuOpen} referenceAsChild flat
    >{button}</HoverCard> : button}
    {policy.disabledReason ? <p role="status" className="m-0 px-3 py-1 text-xs text-slate-600">{policy.disabledReason}</p> : null}
    {policy.error ? <p role="alert" className="m-0 px-3 py-1 text-xs text-red-700">{policy.error}</p> : null}
    {!compact ? summary : null}
  </div>;
}

export default function DispatchHeaderMoreMenu({ contextKey, contextGroup = "more", children, model = {}, interactions = {}, isOptimizationInProgress = false, onSameOdGroupingMutationStateChange = null }) {
  const [confirmCompleteMode, setConfirmCompleteMode] = React.useState(false);
  const day = model.dispatchDayControls;
  const activeDispatchDate = String(day?.activeItem?.dispatchDate || day?.activeDispatchDate || "").trim();
  const hasSnapshot = day?.activeItem?.hasSnapshot === true || Boolean(day?.activeLastOptimizedAt);
  const status = String(day?.activeDispatchStatus || day?.activeStatus?.key || "").trim().toLowerCase();
  const isClosed = day?.activeItem?.isClosed === true
    || ["cloture", "closed"].includes(status)
    || String(day?.activeDispatchState || "").trim().toLowerCase() === "closed";
  const hasPendingMutations = Number.isFinite(Number(model.pendingPlanningMutationCount))
    && Number(model.pendingPlanningMutationCount) > 0;
  const isPlanningBusy = isOptimizationInProgress || hasPendingMutations || interactions.manualProductMixing?.isSaving === true || interactions.productCompartmentFill?.isSaving === true || interactions.compartmentSharing?.isSaving === true || interactions.eveningOrderPool?.isSaving === true;
  const canSelectMode = interactions.canMutatePlanning === true;
  const canConfigureProtection = interactions.canConfigureReassignmentProtection ?? canSelectMode;
  const mode = normalizeDispatchReassignmentMode(interactions.reassignmentRebuildMode);
  const isFastEnabled = resolveDispatchFastReassignmentEnabled(import.meta.env?.VITE_DISPATCH_FAST_REASSIGNMENT_ENABLED);
  React.useEffect(() => { setConfirmCompleteMode(false); }, [contextKey, activeDispatchDate, mode]);
  const pullForward = interactions.pullForward;
  const canGroup = pullForward?.canManage === true && hasSnapshot;
  const groupDisabled = isClosed || isPlanningBusy || !activeDispatchDate
    || pullForward?.isBusy === true || pullForward?.isOpen === true;
  const groupDisabledReason = interactions.isSameOdGroupingMutationPending === true
    ? "Le réglage Groupes inséparables est en cours d’enregistrement. Attendez sa confirmation avant de relancer l’optimisation."
    : hasPendingMutations
      ? "Une mise à jour du planning est en cours. Attendez sa fin avant de relancer l'optimisation ou de réinitialiser le planning."
      : isOptimizationInProgress
        ? "Une optimisation est en cours. Attendez sa fin avant de modifier le planning."
        : isClosed
          ? "Journée clôturée : rouvrez-la avant de réinitialiser le planning ou de relancer l'optimisation."
          : !activeDispatchDate
            ? "Aucune date de dispatch active."
            : pullForward?.isBusy === true
              ? "L’analyse J+1/J+2 est déjà en cours."
              : pullForward?.isOpen === true
                ? "La fenêtre de regroupement est déjà ouverte."
                : "Analyser localement les blocs client/site de J+1 et J+2.";

  return (
    <>
    <DispatchHeaderActionMenu label="Plus" contextGroup={contextGroup} contextKey={contextKey} shouldIgnoreTarget={isColorPopoverTarget} menuClassName="dispatch-header-more-menu">
      {({ isOpen }) => <div className="dispatch-header-more">
        {canSelectMode || canConfigureProtection || canGroup ? (
          <DispatchHeaderMenuSection label="Dispatch">
            {canSelectMode ? (
              <div role="group" aria-label="Réaffectation" data-dispatch-reassignment-mode-control="true">
                <HoverCard
                  title="Mode des actions manuelles"
                  content={(
                    <div className="grid gap-2 text-[0.76rem] leading-5 text-content-copy">
                      <p className="m-0"><strong>Complet</strong> conserve la reconstruction actuelle.</p>
                      <p className="m-0"><strong>Rapide</strong> conserve l’ordre des autres commandes et reconstruit seulement les camions touchés.</p>
                      <p className="m-0 text-muted">Les compartiments, compatibilités et fenêtres horaires restent validés. En Rapide, les heures maximales ne bloquent ni la réaffectation ni le réordonnancement gauche/droite sur un même camion et restent sous la responsabilité du répartiteur.</p>
                    </div>
                  )}
                  placement="top-start"
                  delay={140}
                  maxWidth="min(380px, calc(100vw - 48px))"
                  disabled={!isOpen}
                  referenceAsChild
                  flat
                >
                  <div className="dispatch-header-menu-label">Réaffectation</div>
                </HoverCard>
                {[
                  { key: DISPATCH_REASSIGNMENT_MODE_COMPLETE, label: "Complet", Icon: Squares2X2Icon },
                  { key: DISPATCH_REASSIGNMENT_MODE_FAST, label: "Rapide", Icon: BoltIcon },
                ].map((option) => {
                  const isChecked = mode === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      role="menuitemradio"
                      aria-checked={isChecked}
                      className="dispatch-header-menu-action"
                      data-keep-menu-open={option.key === DISPATCH_REASSIGNMENT_MODE_COMPLETE && !isChecked ? undefined : "true"}
                      disabled={isPlanningBusy || !isFastEnabled}
                      tabIndex={-1}
                      onClick={() => {
                        if (isChecked) return;
                        if (option.key === DISPATCH_REASSIGNMENT_MODE_COMPLETE) {
                          setConfirmCompleteMode(true);
                          return;
                        }
                        interactions.onReassignmentRebuildModeChange?.(option.key);
                      }}
                    >
                      <option.Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                      <span className="min-w-0 flex-1">{option.label}</span>
                      <DispatchHeaderMenuSwitch checked={isChecked} />
                    </button>
                  );
                })}
              </div>
            ) : null}
            {canSelectMode && hasSnapshot ? <ManualProductMixingToggle policy={interactions.manualProductMixing} disabled={isClosed || ["gele", "frozen", "livre", "delivered"].includes(status) || isPlanningBusy || interactions.isOptimizationPreviewInteractionLocked === true} /> : null}
            {canSelectMode ? <CompartmentSharingToggle policy={interactions.compartmentSharing} isMenuOpen={isOpen} disabled={isClosed || ["gele", "frozen", "livre", "delivered"].includes(status) || isPlanningBusy || interactions.isOptimizationPreviewInteractionLocked === true} /> : null}
            {canSelectMode ? <ProductCompartmentFillToggle policy={interactions.productCompartmentFill} isMenuOpen={isOpen}
              disabled={isClosed || ["gele", "frozen", "livre", "delivered"].includes(status) || isPlanningBusy || interactions.isOptimizationPreviewInteractionLocked === true} /> : null}
            {canSelectMode && interactions.optimizationScope?.dispatchPeriod !== "day" ? <EveningOrderPoolMenuItem policy={interactions.eveningOrderPool}
              disabled={isClosed || ["gele", "frozen", "livre", "delivered"].includes(status) || isPlanningBusy || interactions.isOptimizationPreviewInteractionLocked === true} /> : null}
            {canConfigureProtection ? (
              <ProtectAfterReassignmentToggle
                checked={interactions.protectAfterReassignment !== false}
                disabled={isPlanningBusy || interactions.isOptimizationPreviewInteractionLocked === true}
                onChange={interactions.onProtectAfterReassignmentChange}
                isMenuOpen={isOpen}
              />
            ) : null}
            {canGroup ? (
              <button
                type="button"
                role="menuitem"
                aria-label="Regrouper les commandes de J+1 et J+2"
                className="dispatch-header-menu-action"
                disabled={groupDisabled}
                onClick={() => pullForward?.open?.()}
                title={groupDisabledReason}
                tabIndex={-1}
              >
                <CalendarDaysIcon aria-hidden="true" />
                Regrouper J+1/J+2
              </button>
            ) : null}
          </DispatchHeaderMenuSection>
        ) : null}
        <DispatchHeaderMenuSection label="Préférences">
          <DispatchColorIntensityControl menuItem parentOpen={isOpen} />
          <SameOdGroupingToggle
            isMenuOpen={isOpen}
            isOptimizationInProgress={isOptimizationInProgress}
            onMutationStateChange={onSameOdGroupingMutationStateChange}
          />
        </DispatchHeaderMenuSection>
        <div className="dispatch-header-more-portal" role="group" aria-label="Portail chauffeur">
          <DispatchHeaderMenuSection label="Portail chauffeur">
            <div className="dispatch-header-slot">{children}</div>
          </DispatchHeaderMenuSection>
        </div>
      </div>}
    </DispatchHeaderActionMenu>
    {confirmCompleteMode ? <ConfirmationModal
      title="Passer en mode Complet ?"
      description="Les prochaines réaffectations utiliseront le mode Complet à la place du mode Rapide. Confirmez-vous ce changement ?"
      confirmLabel="Passer en mode Complet"
      cancelLabel="Annuler"
      tone="accent" contentDensity="compact" cornerRadius="grain" panelSize="narrow"
      confirmDisabled={!canSelectMode || isPlanningBusy || !isFastEnabled}
      onCancel={() => setConfirmCompleteMode(false)}
      onConfirm={() => {
        if (!canSelectMode || isPlanningBusy || !isFastEnabled) return;
        setConfirmCompleteMode(false);
        interactions.onReassignmentRebuildModeChange?.(DISPATCH_REASSIGNMENT_MODE_COMPLETE);
      }}
    /> : null}
    </>
  );
}
